import moment from "moment";

export const MAX_IMPORT_RECORDS = 2000;

export type AssessmentFieldKey =
  | "Title"
  | "AssessmentType"
  | "Contractor"
  | "Agency"
  | "ReportDate"
  | "Summary"
  | "SerialNumber"
  | "Location"
  | "CapacityAssessmentType"
  | "OverallRating"
  | "OverallRiskRating"
  | "Sensitivity"
  | "ClassificationLevel"
  | "NoOfAssociatedEntitiesScreened"
  | "NoOfIndividualsScreened"
  | "VettingEligibilityRecommendation"
  | "VerificationDate";

export type FieldMapping = Partial<Record<AssessmentFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const ASSESSMENT_FIELDS: Array<{
  key: AssessmentFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "Title", label: "Title", required: true },
  { key: "AssessmentType", label: "Assessment Type", required: true },
  { key: "Contractor", label: "Contractor" },
  { key: "Agency", label: "Agency" },
  { key: "ReportDate", label: "Report Date" },
  { key: "Summary", label: "Summary" },
  { key: "SerialNumber", label: "Serial Number" },
  { key: "Location", label: "Location" },
  { key: "CapacityAssessmentType", label: "Capacity Assessment Type" },
  { key: "OverallRating", label: "Overall Rating" },
  { key: "OverallRiskRating", label: "Overall Risk Rating" },
  { key: "Sensitivity", label: "Sensitivity" },
  { key: "ClassificationLevel", label: "Classification Level" },
  {
    key: "NoOfAssociatedEntitiesScreened",
    label: "No. of Associated Entities Screened",
  },
  { key: "NoOfIndividualsScreened", label: "No. of Individuals Screened" },
  {
    key: "VettingEligibilityRecommendation",
    label: "Vetting Eligibility Recommendation",
  },
  { key: "VerificationDate", label: "Verification Date" },
];

function normalizeStr(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

const normalizeDate = (value: string | null) => {
  if (!value) return null;

  const parsed = moment(value, [
    "YYYY-MM-DD",
    "DD/MM/YYYY",
    "DD-MM-YYYY",
    "MM/DD/YYYY",
    "D/M/YYYY",
    "D-M-YYYY",
  ], true);

  return parsed.isValid() ? parsed.toDate().toLocaleDateString("en-CA") : value;
};

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of ASSESSMENT_FIELDS) {
    const normalizedField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normalizedField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping
) {
  const get = (key: AssessmentFieldKey): string | null => {
    const column = mappings[key];
    if (!column) return null;
    return row[column]?.trim() || null;
  };

  return {
    title: get("Title") ?? "",
    assessmentType: get("AssessmentType"),
    contractor: get("Contractor"),
    agency: get("Agency"),
    reportDate: normalizeDate(get("ReportDate")),
    summary: get("Summary"),
    serialNumber: get("SerialNumber"),
    location: get("Location"),
    capacityAssessmentType: get("CapacityAssessmentType"),
    overallRating: get("OverallRating"),
    overallRiskRating: get("OverallRiskRating"),
    sensitivity: get("Sensitivity"),
    classificationLevel: get("ClassificationLevel"),
    noOfAssociatedEntitiesScreened:
      get("NoOfAssociatedEntitiesScreened") || null,
    noofindividualsScreened: get("NoOfIndividualsScreened") || null,
    vettingEligibilityRecommendation: get("VettingEligibilityRecommendation"),
    verificationDate: normalizeDate(get("VerificationDate")),
  };
}
